import { useCallback, useEffect, useState } from 'react'
import { useAuth } from '@/core/AuthContext'
import type { PhoneConfirmationResult } from '@/core/AuthContext'

export interface UsePhoneAuthOptions {
  /**
   * Called after successful verification with the fresh userData (or null if not found)
   */
  onVerified?: (userData: Awaited<ReturnType<ReturnType<typeof useAuth>['refreshUserData']>>) => void | Promise<void>
  /**
   * If true, sets skipAutoCreateUser before verification (trainer flow)
   */
  skipAutoCreate?: boolean
  /**
   * Debug logger callback (client flow uses debug panel)
   */
  addDebugLog?: (msg: string) => void
}

export interface UsePhoneAuthReturn {
  // State
  phoneNumber: string
  setPhoneNumber: (value: string) => void
  verificationCode: string
  setVerificationCode: (value: string) => void
  confirmation: PhoneConfirmationResult | null
  loading: boolean
  error: string | null
  verified: boolean
  
  // Actions
  sendCode: (e: React.FormEvent) => Promise<void>
  verifyCode: (e: React.FormEvent) => Promise<void>
  reset: () => void
  
  // Auth context pass-through
  user: ReturnType<typeof useAuth>['user']
  userData: ReturnType<typeof useAuth>['userData']
  authLoading: ReturnType<typeof useAuth>['loading']
}

/**
 * Shared phone authentication logic for trainer and client login pages.
 * Handles phone number input, verification code flow, and recaptcha visibility.
 */
export function usePhoneAuth(options: UsePhoneAuthOptions = {}): UsePhoneAuthReturn {
  const { onVerified, skipAutoCreate = false, addDebugLog = () => {} } = options
  
  const {
    user,
    userData,
    loading: authLoading,
    signInWithPhone,
    verifyPhoneCode,
    setSkipAutoCreateUser,
    refreshUserData,
  } = useAuth()

  const [phoneNumber, setPhoneNumber] = useState('')
  const [verificationCode, setVerificationCode] = useState('')
  const [confirmation, setConfirmation] = useState<PhoneConfirmationResult | null>(null)
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [verified, setVerified] = useState(false)

  // Manage recaptcha visibility
  useEffect(() => {
    document.body.classList.add('show-recaptcha')
    return () => {
      document.body.classList.remove('show-recaptcha')
      if (skipAutoCreate) {
        setSkipAutoCreateUser(false)
      }
    }
  }, [setSkipAutoCreateUser, skipAutoCreate])

  const sendCode = useCallback(async (e: React.FormEvent) => {
    e.preventDefault()
    setLoading(true)
    setError(null)
    addDebugLog('=== Send Code Clicked ===')
    addDebugLog(`Phone: ${phoneNumber}`)

    try {
      let formattedPhone = phoneNumber.trim()
      if (!formattedPhone.startsWith('+')) {
        formattedPhone = `+1${formattedPhone.replace(/\D/g, '')}`
      }

      const timeoutPromise = new Promise<never>((_, reject) => {
        setTimeout(() => reject(new Error('Native auth timeout after 30s')), 30000)
      })

      const result = await Promise.race([signInWithPhone(formattedPhone), timeoutPromise])
      addDebugLog(`✅ signInWithPhone returned: kind=${result.kind}`)
      setConfirmation(result)
    } catch (err: any) {
      console.error('Phone auth error:', err)
      addDebugLog(`❌ Error: ${err?.message || JSON.stringify(err)}`)
      setError(err.message || 'Failed to send verification code')
    } finally {
      setLoading(false)
    }
  }, [phoneNumber, signInWithPhone, addDebugLog])

  const verifyCode = useCallback(async (e: React.FormEvent) => {
    e.preventDefault()
    if (!confirmation) return

    setLoading(true)
    setError(null)
    addDebugLog('=== Verify Code ===')
    addDebugLog(`Code: ${verificationCode}`)

    if (skipAutoCreate) {
      setSkipAutoCreateUser(true)
    }

    try {
      await verifyPhoneCode(confirmation, verificationCode)
      addDebugLog('✅ Verified successfully')
      setVerified(true)

      addDebugLog('Post-verify: refreshing userData...')
      const freshUserData = await refreshUserData()

      if (onVerified) {
        await onVerified(freshUserData)
      }
    } catch (err: any) {
      console.error('[usePhoneAuth] Verification error:', err)
      addDebugLog(`❌ Verify error: ${err?.message || err}`)
      setError(err?.message || 'Verification failed. Please try again.')
      if (skipAutoCreate) {
        setSkipAutoCreateUser(false)
      }
    } finally {
      setLoading(false)
    }
  }, [confirmation, verificationCode, verifyPhoneCode, refreshUserData, onVerified, skipAutoCreate, setSkipAutoCreateUser, addDebugLog])

  const reset = useCallback(() => {
    setConfirmation(null)
    setVerificationCode('')
    setError(null)
    setVerified(false)
    if (skipAutoCreate) {
      setSkipAutoCreateUser(false)
    }
  }, [setSkipAutoCreateUser, skipAutoCreate])

  return {
    // State
    phoneNumber,
    setPhoneNumber,
    verificationCode,
    setVerificationCode,
    confirmation,
    loading,
    error,
    verified,
    
    // Actions
    sendCode,
    verifyCode,
    reset,
    
    // Auth context pass-through
    user,
    userData,
    authLoading,
  }
}
